import { useEffect } from "react";

/**
 * Apple-style scroll reveal.
 * Any element with [data-reveal] (or nested via [data-reveal-group])
 * fades + rises into view as it enters the viewport.
 *
 * Usage:
 *   <div data-reveal>...</div>
 *   <div data-reveal-group>
 *     <span data-reveal-child>line 1</span>
 *     <span data-reveal-child>line 2</span>
 *   </div>
 */
export default function useScrollReveal() {
  useEffect(() => {
    if (typeof window === "undefined") return;

    const prefersReduced = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;
    if (prefersReduced) {
      document
        .querySelectorAll("[data-reveal], [data-reveal-child], [data-reveal-zoom]")
        .forEach((el) => el.classList.add("is-revealed"));
      return;
    }

    const els = document.querySelectorAll(
      "[data-reveal], [data-reveal-child], [data-reveal-zoom]",
    );
    if (!els.length) return;

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-revealed");
            io.unobserve(entry.target);
          }
        });
      },
      { rootMargin: "0px 0px -10% 0px", threshold: 0.12 },
    );

    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
}
